/* eslint-disable no-unused-expressions */
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import cloneDeep from 'lodash/cloneDeep';
// modules
import Validation from 'modules/Validation';
import Logger from 'modules/Logger';
import ScripsDb from 'modules/Db';
// typings
import { Exchange, SegmentType } from 'modules/Search/constants';
// utils
import {
  decomposeSegment,
  decomposeExchangeToken,
  decomposeInstrumentKey,
  getTimestampFromDataSchema,
} from './utils';
// sources
import { SCRIPS_DEFAULT } from './constants';

dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
dayjs.extend(timezone);
export default class BodSearch {
  public noResults = false;
  public searchResults: DataSchema[] = [];
  public subHeaderActive = 'ALL';

  scrips: Scrips | null = null;

  getSearchResultsBasedOnPropertyAsync = async (
    instrumentType: InstrumentType,
    segment: SegmentType,
    token: string,
    property: 'token' | 'instrumentKey',
  ): Promise<DataSchema | null> => {
    let searchResult: DataSchema | null = null;
    if (this.scrips) {
      searchResult = this.getSearchResultsBasedOnProperty(
        instrumentType,
        segment,
        token,
        property,
      );
    } else {
      const searchToken = property === 'token' ? `${segment}|${token}` : token;
      const propertyToCheck = property === 'token' ? 'exchangeToken' : 'id';
      if (!searchResult && token) {
        searchResult = await ScripsDb.getScripSearchResult(
          searchToken,
          propertyToCheck,
        );
      }

      if (!searchResult && token) {
        searchResult = await ScripsDb.getScripSearchResult(
          searchToken,
          propertyToCheck,
        );
      }
    }

    return searchResult;
  };

  searchBasedOnPropertyAsync = async (
    value: string,
    property: 'token' | 'instrumentKey',
  ): Promise<DataSchema | null> => {
    const { segment, token } = decomposeExchangeToken(value);
    const { series } = decomposeSegment(segment);
    const updatedToken = property === 'token' ? token : value;

    this.subHeaderActive = 'ALL';
    if (series === 'FO' || series === 'COM') {
      const futResult = await this.getSearchResultsBasedOnPropertyAsync(
        'FUT',
        segment,
        updatedToken,
        property,
      );
      if (futResult) {
        return futResult;
      }

      const optResult = await this.getSearchResultsBasedOnPropertyAsync(
        'OPT',
        segment,
        updatedToken,
        property,
      );
      return optResult || null;
    }

    if (series === 'INDEX') {
      const indexResult = await this.getSearchResultsBasedOnPropertyAsync(
        'INDEX',
        segment,
        updatedToken,
        property,
      );
      return indexResult;
    }

    const equityResult = await this.getSearchResultsBasedOnPropertyAsync(
      'EQ',
      segment,
      updatedToken,
      property,
    );
    return equityResult;
  };

  getSearchResultsBasedOnProperty = (
    instrumentType: InstrumentType,
    segment: SegmentType,
    token: string,
    property: 'token' | 'instrumentKey',
  ): DataSchema | null => {
    let searchResult = null;
    const segmentData = this.scrips?.[instrumentType];

    if (segmentData && segmentData[segment]) {
      segmentData[segment]?.every((item: DataSchema) => {
        if (
          item[property].toString() === token ||
          (instrumentType === 'INDEX' && item.name.toString() === token)
        ) {
          searchResult = item;
          return false;
        }

        return true;
      });
    }

    return searchResult;
  };

  searchBasedOnProperty = (
    value: string,
    property: 'token' | 'instrumentKey',
  ): DataSchema | null => {
    const { segment, token } = decomposeExchangeToken(value);
    const { series } = decomposeSegment(segment);
    const updatedToken = property === 'token' ? token : value;

    this.subHeaderActive = 'ALL';
    if (series === 'FO' || series === 'COM') {
      return (
        this.getSearchResultsBasedOnProperty(
          'FUT',
          segment,
          updatedToken,
          property,
        ) ||
        this.getSearchResultsBasedOnProperty(
          'OPT',
          segment,
          updatedToken,
          property,
        )
      );
    }

    if (series === 'INDEX') {
      return this.getSearchResultsBasedOnProperty(
        'INDEX',
        segment,
        updatedToken,
        property,
      );
    }

    return this.getSearchResultsBasedOnProperty(
      'EQ',
      segment,
      updatedToken,
      property,
    );
  };

  protected clear = () => {
    // clear memory
    this.scrips = cloneDeep(SCRIPS_DEFAULT);
  };

  getTokensToSymbolMap = (
    tokens: ScripFile[LowerCasedExchange]['to']['TOKENS']['NSE_INDICES'] = {},
  ) => {
    const currTokens: {
      [key in keyof ScripFile[LowerCasedExchange]['to']['TOKENS']['NSE_INDICES']]: string;
    } = {};

    Object.keys(tokens).forEach(key => {
      currTokens[tokens[key]] = key;
    });

    return currTokens;
  };

  protected parseIndices = (
    data: ScripFile[LowerCasedExchange]['i'],
    exchange: Exchange,
    tokens: ScripFile[LowerCasedExchange]['to']['TOKENS']['NSE_INDIES'] = {},
  ) => {
    if (exchange === Exchange.NSE) {
      const currTokens = this.getTokensToSymbolMap(tokens);

      for (let l = 0; l < data.length; l++) {
        const [index, instrumentKey, token] = data[l].split(',');
        const { segment } = decomposeInstrumentKey(instrumentKey);
        const exchangeToken = `${segment}|${index}`;

        const result: DataSchema = {
          exchange,
          symbol: currTokens[token] || index,
          name: index,
          token: token || index,
          series: 'INDEX',
          instrument: 'INDEX',
          displayName: index,
          exchangeToken,
          instrumentKey,
          segment,
          instrumentType: 'INDEX',
        };

        this.scrips?.INDEX[segment]?.push(result);
      }
    } else if (exchange === Exchange.BSE) {
      for (let l = 0; l < data.length; l++) {
        const [index, instrumentKey] = data[l].split(',');
        const { segment } = decomposeInstrumentKey(instrumentKey);
        const exchangeToken = `${segment}|${index}`;

        const result: DataSchema = {
          exchange,
          symbol: index,
          name: index,
          token: tokens[index] || index,
          series: 'INDEX',
          instrument: 'INDEX',
          displayName: index,
          exchangeToken,
          instrumentKey,
          segment,
          instrumentType: 'INDEX',
        };

        this.scrips?.INDEX?.[segment]?.push(result);
      }
    } else {
      for (let l = 0; l < data.length; l++) {
        const [index, instrumentKey] = data[l].split(',');
        const { segment } = decomposeInstrumentKey(instrumentKey);
        const exchangeToken = `${segment}|${index}`;

        const result: DataSchema = {
          exchange,
          symbol: index,
          name: index,
          token: index,
          series: 'INDEX',
          instrument: 'INDEX',
          displayName: index,
          exchangeToken,
          instrumentKey,
          segment,
          instrumentType: 'INDEX',
        };

        this.scrips?.INDEX?.[segment]?.push(result);
      }
    }
  };

  protected parseEquities = (
    data: ScripFile[LowerCasedExchange]['eq'],
    exchange: Exchange.BSE | Exchange.NSE,
  ) => {
    for (let l = 0; l < data.length; l++) {
      const scrip = data[l];

      const [
        token,
        symbol,
        name,
        series,
        lotSize,
        tickSize,
        maxQuantity,
        isin,
        instrumentKey,
      ] = scrip.split(',');

      const { segment } = decomposeInstrumentKey(instrumentKey);
      const exchangeToken = `${segment}|${token}`;

      const obj: DataSchema = {
        instrumentKey,
        exchange,
        token,
        symbol,
        name,
        lotSize: Number(lotSize),
        tickSize: Number(tickSize),
        maxQuantity: Number(maxQuantity),
        instrument: series as DataSchema['instrument'],
        series: series as Series,
        displayName:
          symbol + (segment === SegmentType.NSE_EQ ? ` ${series}` : ''),
        exchangeToken,
        isin,
        segment,
        instrumentType: 'EQ',
      };

      this.scrips?.EQ[segment]?.push(obj);
    }
  };

  protected parseFutures = (
    data: ScripFile[LowerCasedExchange]['f'],
    exchange: Exchange.NSE | Exchange.BSE,
    isWeekly = false,
  ) => {
    data.forEach(futures => {
      // first elements is "underlying token"
      // all next elements are placed in loop:
      // expiry, token, lotSize, tickSize, maxQuantity, instrumentKey, tradedValue
      // expiry, token, lotSize, tickSize, maxQuantity, instrumentKey, tradedValue
      // ......
      const assetToken = futures[0].toString();

      const asset =
        this.searchBasedOnProperty(
          `${exchange}_INDEX|${assetToken}`,
          'token',
        ) ||
        this.searchBasedOnProperty(`${exchange}_EQ|${assetToken}`, 'token');

      if (asset) {
        for (let i = 1; i < futures.length; i += 7) {
          const expiry = futures[i] as string;
          const token = futures[i + 1];
          const lotSize = futures[i + 2];
          const tickSize = futures[i + 3];
          const maxQuantity = futures[i + 4];
          const instrumentKey = futures[i + 5] as string;

          const dateObj = this.getDateMonthYear(expiry);

          const { segment } = decomposeInstrumentKey(instrumentKey);
          const exchangeToken = `${segment}|${token}`;

          const obj: DataSchema = {
            instrumentKey,
            exchange,
            token,
            symbol: `${asset.symbol} ${dateObj.date}${this.getShortMonth(
              dateObj.month,
            )}${dateObj.year} FUT`,
            name: asset.name,
            expiryDate: dateObj.date,
            expiryMonth: dateObj.month,
            expiryYear: dateObj.year,
            instrument: 'FUT',
            series: 'FO',
            lotSize: Number(lotSize),
            tickSize: Number(tickSize),
            maxQuantity: Number(maxQuantity),
            underlyingSymbol: asset.symbol,
            displayName: `${asset.symbol} ${dateObj.date}${this.getShortMonth(
              dateObj.month,
            )}${dateObj.year} FUT`,
            exchangeToken,
            underlyingExchangeToken: asset.exchangeToken,
            isWeekly,
            segment,
            instrumentType: 'FUT',
          };

          if (asset.optionsAvailable) {
            obj.optionsAvailable = true;
          }

          this.scrips?.FUT?.[segment]?.push(obj);
        }
      }
    });
  };

  protected parseOptions = (
    options: ScripFile[LowerCasedExchange]['o'],
    exchange: Exchange.NSE | Exchange.BSE,
    isWeekly = false,
  ) => {
    options.forEach(optionsPerScripArray => {
      // first element in array is underlying token (assetToken)
      const assetToken = optionsPerScripArray[0];

      const asset =
        this.searchBasedOnProperty(
          `${exchange}_INDEX|${assetToken}`,
          'token',
        ) ||
        this.searchBasedOnProperty(`${exchange}_EQ|${assetToken}`, 'token');

      if (asset) {
        asset.optionsAvailable = true;

        for (let i = 1; i < optionsPerScripArray.length; i++) {
          const optionInnerArray = optionsPerScripArray[i] as (
            | number
            | string
          )[];

          // structure for inner array:
          // [
          //   expiry, lotSize, tickSize, maxQuantity,
          //   <CE token>, <PE token>, <strike price>, <CE instrumentKey>, <PE instrumentKey>, <CE traded value>, <PE traded value>,
          //   <CE token>, <PE token>, <strike price>, <CE instrumentKey>, <PE instrumentKey>, <CE traded value>, <PE traded value>,
          //   ........
          //   ......
          //   ...
          //   <CE token>, <PE token>, <strike price>, <CE instrumentKey>, <PE instrumentKey>, <CE traded value>, <PE traded value>,
          // ],

          const expiry = optionInnerArray[0] as string;
          const lotSize = optionInnerArray[1] as number;
          const tickSize = optionInnerArray[2] as number;
          const maxQuantity = optionInnerArray[3] as number;

          const dateObj = this.getDateMonthYear(expiry);

          for (let j = 4; j < optionInnerArray.length; j += 7) {
            const ceToken = optionInnerArray[j] as number;
            const peToken = optionInnerArray[j + 1] as number;
            const strikePrice = optionInnerArray[j + 2] as number;
            const ceInstrumentKey = optionInnerArray[j + 3] as string;
            const peInstrumentKey = optionInnerArray[j + 4] as string;
            const { segment } = decomposeInstrumentKey(ceInstrumentKey);
            const ceExchangeToken = `${segment}|${ceToken}`;
            const peExchangeToken = `${segment}|${peToken}`;

            const baseObj: Omit<
              DataSchema,
              | 'optionType'
              | 'symbol'
              | 'displayName'
              | 'exchangeToken'
              | 'token'
              | 'tradedValue'
              | 'instrumentKey'
            > = {
              exchange,
              strikePrice,
              expiryDate: dateObj.date,
              expiryMonth: dateObj.month,
              expiryYear: dateObj.year,
              series: 'FO',
              instrument: 'OPT',
              name: asset.name,
              underlyingSymbol: asset.symbol,
              lotSize,
              tickSize,
              maxQuantity,
              isWeekly,
              optionsAvailable: true,
              segment,
              underlyingExchangeToken: asset.exchangeToken.toString(),
              instrumentType: 'OPT',
            };

            const ceObj: DataSchema = {
              ...baseObj,
              token: ceToken,
              optionType: 'CE',
              symbol: `${asset.symbol} ${dateObj.date}${this.getShortMonth(
                dateObj.month,
              )}${dateObj.year} ${strikePrice} CE`,
              displayName: `${asset.symbol} ${dateObj.date}${this.getShortMonth(
                dateObj.month,
              )}${dateObj.year} ${strikePrice} CE`,
              instrumentKey: ceInstrumentKey,
              exchangeToken: ceExchangeToken,
            };

            const peObj: DataSchema = {
              ...baseObj,
              token: peToken,
              optionType: 'PE',
              symbol: `${asset.symbol} ${dateObj.date}${this.getShortMonth(
                dateObj.month,
              )}${dateObj.year} ${strikePrice} PE`,
              instrument: 'OPT',
              displayName: `${asset.symbol} ${dateObj.date}${this.getShortMonth(
                dateObj.month,
              )}${dateObj.year} ${strikePrice} PE`,
              instrumentKey: peInstrumentKey,
              exchangeToken: peExchangeToken,
            };

            this.scrips?.OPT?.[segment]?.push(ceObj);
            this.scrips?.OPT?.[segment]?.push(peObj);
          }
        }
      }
    });
  };

  protected parseCurrencyFutures = (
    data: ScripFile[LowerCasedExchange]['cf'],
    exchange: Exchange,
    tokens: ScripFile[LowerCasedExchange]['to']['TOKENS']['BCD_FO'],
  ) => {
    const currTokens = this.getTokensToSymbolMap(tokens);

    data.forEach(futures => {
      // first elements is "underlying token"
      // all next elements are placed in loop:
      // expiry, token, lotSize, tickSize, maxQuantity, instrumentKey, tradedValue
      // expiry, token, lotSize, tickSize, maxQuantity, instrumentKey, tradedValue
      // ......
      const assetToken = futures[0];

      const asset = {
        name: currTokens[assetToken],
        symbol: currTokens[assetToken],
      };

      if (asset) {
        for (let i = 1; i < futures.length; i += 7) {
          const expiry = futures[i] as string;
          const token = futures[i + 1];
          const lotSize = futures[i + 2];
          const tickSize = futures[i + 3];
          const maxQuantity = futures[i + 4];
          const instrumentKey = futures[i + 5] as string;

          const dateObj = this.getDateMonthYear(expiry);

          const { segment } = decomposeInstrumentKey(instrumentKey);
          const exchangeToken = `${segment}|${token}`;
          const isCommodity = Validation.isCommodity(segment);

          const obj: DataSchema = {
            exchange,
            token,
            symbol: `${asset.symbol} ${dateObj.date}${this.getShortMonth(
              dateObj.month,
            )}${dateObj.year} FUT`,
            name: asset.name,
            expiryDate: dateObj.date,
            expiryMonth: dateObj.month,
            expiryYear: dateObj.year,
            instrument: 'FUT',
            series: 'FO',
            lotSize: Number(lotSize),
            tickSize: Number(tickSize),
            maxQuantity: Number(maxQuantity),
            underlyingSymbol: asset.symbol,
            displayName: `${asset.symbol} ${dateObj.date}${this.getShortMonth(
              dateObj.month,
            )}${dateObj.year} FUT`,
            exchangeToken,
            instrumentKey,
            ...(isCommodity && {
              underlyingExchangeToken: `${exchange}_COM|${assetToken}`,
            }),
            optionsAvailable: isCommodity,
            segment,
            instrumentType: 'FUT',
          };

          this.scrips?.FUT?.[segment]?.push(obj);
        }
      }
    });
  };

  protected parseCurrencyOptions = (
    options:
      | ScripFile[LowerCasedExchange]['co']
      | ScripFile[LowerCasedExchange]['wco'],
    exchange: Exchange,
    tokens: ScripFile[LowerCasedExchange]['to']['TOKENS']['BCD_FO'],
    isWeekly = false,
  ) => {
    const currTokens = this.getTokensToSymbolMap(tokens);

    options.forEach(optionsPerScripArray => {
      // first element in array is underlying token (assetToken)
      const assetToken = optionsPerScripArray[0];

      const availableToken = currTokens[assetToken];

      const asset = availableToken
        ? {
            name: availableToken,
            symbol: availableToken,
          }
        : null;

      if (asset) {
        for (let i = 1; i < optionsPerScripArray.length; i++) {
          const optionInnerArray = optionsPerScripArray[i] as (
            | number
            | string
          )[];

          // [
          //   expiry, lotSize, tickSize, maxQuantity,
          //   <CE token>, <PE token>, <strike price>, <CE instrument key>, <PE instrument key>, <CE traded value>, <PE traded value>,
          //   <CE token>, <PE token>, <strike price>, <CE instrument key>, <PE instrument key>, <CE traded value>, <PE traded value>,
          //   ........
          //   ......
          //   ...
          //   <CE token>, <PE token>, <strike price>, <CE instrument key>, <PE instrument key>, <CE traded value>, <PE traded value>
          // ]

          const expiry = optionInnerArray[0] as string;
          const lotSize = optionInnerArray[1] as number;
          const tickSize = optionInnerArray[2] as number;
          const maxQuantity = optionInnerArray[3] as number;

          const dateObj = this.getDateMonthYear(expiry);

          for (let j = 4; j < optionInnerArray.length; j += 7) {
            const ceToken = optionInnerArray[j] as number;
            const peToken = optionInnerArray[j + 1] as number;
            const strikePrice = optionInnerArray[j + 2] as number;
            const ceInstrumentKey = optionInnerArray[j + 3] as string;
            const peInstrumentKey = optionInnerArray[j + 4] as string;
            const { segment } = decomposeInstrumentKey(ceInstrumentKey);
            const isCommodity = Validation.isCommodity(segment);
            const ceExchangeToken = `${segment}|${ceToken}`;
            const peExchangeToken = `${segment}|${peToken}`;

            const { displayName, symbol } = this.geOPTSymbolAndDisplayNames(
              dateObj,
              strikePrice,
              asset.symbol,
            );

            const baseObj: Omit<
              DataSchema,
              | 'optionType'
              | 'symbol'
              | 'displayName'
              | 'exchangeToken'
              | 'token'
              | 'tradedValue'
              | 'instrumentKey'
            > = {
              strikePrice,
              exchange,
              expiryDate: dateObj.date,
              expiryMonth: dateObj.month,
              expiryYear: dateObj.year,
              series: 'FO',
              instrument: 'OPT',
              name: asset.name,
              underlyingSymbol: asset.symbol,
              lotSize,
              tickSize,
              maxQuantity,
              ...(isCommodity && {
                underlyingExchangeToken: `${exchange}_COM|${assetToken}`,
              }),
              optionsAvailable: isCommodity,
              isWeekly,
              segment,
              instrumentType: 'OPT',
            };

            const ceObj: DataSchema = {
              ...baseObj,
              token: ceToken,
              optionType: 'CE',
              symbol: symbol.CE,
              displayName: displayName.CE,
              exchangeToken: ceExchangeToken,
              instrumentKey: ceInstrumentKey,
            };

            const peObj: DataSchema = {
              ...baseObj,
              token: peToken,
              optionType: 'PE',
              symbol: symbol.PE,
              displayName: symbol.PE,
              exchangeToken: peExchangeToken,
              instrumentKey: peInstrumentKey,
            };

            this.scrips?.OPT?.[segment]?.push(ceObj);
            this.scrips?.OPT?.[segment]?.push(peObj);
          }
        }
      }
    });
  };

  private getShortMonth = (i: string | number) => {
    const index = Number(i);

    const shortMonths = [
      '',
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ];

    return shortMonths[index];
  };

  private getDateMonthYear = (dateStr: string) => {
    const obj = {
      date: '',
      month: '',
      year: '',
    };

    if (dateStr.length === 5) {
      obj.date = `0${dateStr.substr(0, 1)}`;
      obj.month = dateStr.substr(1, 2);
      obj.year = dateStr.substr(3, 2);
    } else {
      obj.date = dateStr.substr(0, 2);
      obj.month = dateStr.substr(2, 2);
      obj.year = dateStr.substr(4, 2);
    }

    return obj;
  };

  private geOPTSymbolAndDisplayNames = (
    {
      date,
      month,
      year,
    }: {
      date: string;
      month: string;
      year: string;
    },
    strikePrice: number,
    symbol: string,
  ) => {
    const symbolName = {
      CE: `${symbol} ${date}${this.getShortMonth(
        month,
      )}${year} ${strikePrice} CE`,
      PE: `${symbol} ${date}${this.getShortMonth(
        month,
      )}${year} ${strikePrice} PE`,
    };
    const displayName = {
      CE: `${symbol} ${date}${this.getShortMonth(
        month,
      )}${year} ${strikePrice} CE`,
      PE: `${symbol} ${date}${this.getShortMonth(
        month,
      )}${year} ${strikePrice} CE`,
    };

    return {
      displayName,
      symbol: symbolName,
    };
  };

  getUpdatedSegmentForOptions = (segment: SegmentType, exchange: Exchange) => {
    let updatedSegment = segment;

    if (segment.includes('INDEX') || segment.includes('EQ')) {
      if (exchange === Exchange.NSE) {
        updatedSegment = SegmentType.NSE_FO;
      } else if (exchange === Exchange.BSE) {
        updatedSegment = SegmentType.BSE_FO;
      }
    } else if (segment.includes('COM')) {
      if (exchange === Exchange.NSE) {
        updatedSegment = SegmentType.NSE_COM;
      } else {
        updatedSegment = SegmentType.MCX_FO;
      }
    }

    return updatedSegment;
  };

  public getOptionExpiries = async (
    exchangeToken: string,
    spotToken: string,
  ): Promise<OptionExpirySchema[]> => {
    const { segment } = decomposeExchangeToken(exchangeToken);
    const { exchange } = decomposeSegment(segment);
    const expiries: number[] = [];
    const expiryWithIsWeekly: OptionExpirySchema[] = [];
    const updatedSegment = this.getUpdatedSegmentForOptions(segment, exchange);

    try {
      let scripResult: DataSchema[] = [];
      if (this.scrips) {
        scripResult = this.scrips?.OPT?.[updatedSegment] ?? [];
      } else {
        const startDate = new Date().getTime();

        const scripDbResult: ScripDbRow[] = await ScripsDb.getDBSearchResults(
          'scrips',
          '[data.instrumentType+data.segment+data.underlyingExchangeToken]',
          ['OPT', updatedSegment, spotToken || ''],
        );

        const endDate = new Date().getTime();
        Logger.log(
          'SEARCH',
          'Performance: getOptionExpiries in',
          endDate - startDate,
          'in ms',
        );
        scripResult = scripDbResult.map(item => item.data);
      }

      scripResult?.forEach(item => {
        const date = getTimestampFromDataSchema(item);
        if (
          item.underlyingExchangeToken === spotToken &&
          !expiries.includes(date)
        ) {
          expiries.push(date);
          expiryWithIsWeekly.push({ date, isWeekly: !!item?.isWeekly });
        }
      });
    } catch (error) {
      return [];
    }

    return expiryWithIsWeekly.sort((a, b) => a.date - b.date);
  };

  public getFutureScripForExpiry = async (
    exchangeToken: string,
  ): Promise<DataSchema | undefined> => {
    const { segment } = decomposeExchangeToken(exchangeToken);
    const { exchange } = decomposeSegment(segment);
    const updatedSegment = this.getUpdatedSegmentForOptions(segment, exchange);

    const currentDate = dayjs()
      .utc()
      .add(5.5, 'hour');

    let scripResult: DataSchema[] = [];

    if (this.scrips) {
      scripResult = this.scrips?.FUT[updatedSegment] || [];
    } else {
      const startDate = new Date().getTime();
      let scripDbResult: ScripDbRow[] = [];
      scripDbResult = await ScripsDb.getDBSearchResults(
        'scrips',
        '[data.instrumentType+data.segment+data.underlyingExchangeToken]',
        ['FUT', updatedSegment, exchangeToken],
      );
      const endDate = new Date().getTime();
      Logger.log(
        'SEARCH',
        'Performance: getFutureScripForExpiry in',
        endDate - startDate,
        'in ms',
      );
      scripResult = scripDbResult.map(item => item.data);
    }

    let sortedFutureExpiryScrips: DataSchema[] = [];
    if (scripResult.length) {
      sortedFutureExpiryScrips = scripResult
        ?.filter(scrip => scrip.underlyingExchangeToken === exchangeToken)
        ?.sort(
          (a, b) =>
            getTimestampFromDataSchema(a) - getTimestampFromDataSchema(b),
        );
    }

    return sortedFutureExpiryScrips?.find(scrip => {
      const { expiryYear, expiryMonth, expiryDate } = scrip;
      const expiry_date = dayjs(`20${expiryYear}-${expiryMonth}-${expiryDate}`)
        .utc()
        .add(5.5, 'hour');

      return currentDate.isSameOrBefore(expiry_date, 'd');
    });
  };

  public getOptionStrikesForExpiry = async (
    exchangeToken: string,
    expiry: number,
  ): Promise<StrikeSchema[]> => {
    const { segment } = decomposeExchangeToken(exchangeToken);
    const { exchange } = decomposeSegment(segment);
    const updatedSegment = this.getUpdatedSegmentForOptions(segment, exchange);

    const result: StrikeSchema[] = [];
    const tokens: { [x: number]: StrikeSchema } = {};

    let scripResult: DataSchema[] = [];

    if (this.scrips) {
      scripResult = this.scrips?.OPT[updatedSegment] || [];
    } else {
      const startDate = new Date().getTime();
      let scripDbResult: ScripDbRow[] = [];
      scripDbResult = await ScripsDb.getDBSearchResults(
        'scrips',
        '[data.instrumentType+data.segment+data.underlyingExchangeToken]',
        ['OPT', updatedSegment, exchangeToken],
      );
      const endDate = new Date().getTime();
      Logger.log(
        'SEARCH',
        'Performance: getOptionStrikesForExpiry in',
        endDate - startDate,
        'in ms',
      );
      scripResult = scripDbResult.map(item => item.data);
    }

    if (scripResult.length) {
      scripResult.forEach(scrip => {
        const expiryDate = getTimestampFromDataSchema(scrip);

        if (
          scrip.underlyingExchangeToken === exchangeToken &&
          expiry === expiryDate
        ) {
          const strikeObject: StrikeSchema = tokens[
            Number(scrip.strikePrice)
          ] || {
            strike: Number(scrip.strikePrice),
            exchangeTokens: {
              CE: '',
              PE: '',
            },
            instrumentKeys: {
              CE: '',
              PE: '',
            },
          };

          strikeObject.exchangeTokens[scrip.optionType as OptionTypes] =
            scrip.exchangeToken;
          strikeObject.instrumentKeys[scrip.optionType as OptionTypes] =
            scrip.instrumentKey;

          if (!tokens[Number(scrip.strikePrice)]) {
            tokens[Number(scrip.strikePrice)] = strikeObject;
            result.push(strikeObject);
          }
        }
      });
    }

    return result.sort((scrip1, scrip2) =>
      (scrip1.strike > scrip2.strike ? 1 : -1),
    );
  };
}
